/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import PropTypes from 'prop-types';

import React from 'react';
import classnames from 'classnames';

import Localized from '../../../shared/components/Localized';
import _ from 'lodash';
import LoadingSpinner from '../Spinners/LoadingSpinner.js';

import Modal from './Modal';
import DismissModal from './DismissModal';

import SubscriptionGrid from '../../../shared/components/SubscriptionGrid';

const severities = [
  {
    apiKey: 'major',
    localizationKey: 'disruption',
    imgUrl: '/assets/icons/icon-major.png'
  },
  {
    apiKey: 'minor',
    localizationKey: 'degradation',
    imgUrl: '/assets/icons/icon-minor.png'
  },
  {
    apiKey: 'maintenance',
    localizationKey: 'maintenance',
    imgUrl: '/assets/icons/icon-maintenance.png'
  }
];

export default class InstanceSubscribeModal extends React.Component {
  static propTypes = {
    submit: PropTypes.func,
    closeModal: PropTypes.func,
    instanceKey: PropTypes.string,
    requests: PropTypes.object,
    isOpen: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      email: '',
      emailValid: false,
      language: 'en',
      all: true,
      custom: false,
      instanceSubscription: {},
      subscriptionValid: false,
      submitted: false
    };

    this.onLanguageChange = this.onLanguageChange.bind(this);
    this.onEmailChange = this.onEmailChange.bind(this);
    this.validateSubscription = this.validateSubscription.bind(this);
    this.clickSubscribeToAll = this.clickSubscribeToAll.bind(this);
    this.clickSubscribeToCustom = this.clickSubscribeToCustom.bind(this);
    this.clickAllServices = this.clickAllServices.bind(this);
    this.clickService = this.clickService.bind(this);
    this.clickServiceSeverity = this.clickServiceSeverity.bind(this);
    this.clickSubmit = this.clickSubmit.bind(this);
    this.getSubscription = this.getSubscription.bind(this);
  }

  onLanguageChange(event) {
    const _selectedLanguage = event.target.value;
    this.setState({ language: _selectedLanguage });
    const fullName = this.props.requests.locales.value.filter((locale) => {
      return locale.code === _selectedLanguage;
    })
      .map((obj) => {
        return obj.name;
      })[0];
  }

  onEmailChange(event) {
    // If currently invalid then check for validity
    const email = event.target.value;
    const emailValidation = /^(([^<>()\[\]\\.,;:\s@']+(\.[^<>()\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const emailValid = email.match(emailValidation) !== null;

    this.setState({ email, emailValid });
  }

  /**
   * Validates subscription and updates state
   *
   * Valid if subscribing to All or subscribing to Custom and some Service/Severity pairs
   * are checked
   *
   * @return
   */
  validateSubscription(subscription) {
    // Subscription valid if type is all or type is custom and some are checked
    if (this.state.all) return this.setState({ subscriptionValid: true });

    const someChecked = _.some(subscription.services, (service) => {
      return _.some((service.severities));
    });

    if (this.state.custom && someChecked) return this.setState({ subscriptionValid: true });

    return this.setState({ subscriptionValid: false });
  }

  // All Notifications on Instance
  clickSubscribeToAll() {
    // Subscribe to all notifications, subscription is assured valid
    this.setState({ all: true, custom: false, subscriptionValid: true });
  }

  // Customize subscription
  // Opens Subscription Grid
  clickSubscribeToCustom() {
    // Defaults to True for all Services/Severies Subscription Combinations
    const instanceSubscription = { services: {} };
    this.props.requests.instancePreview.value.Services.map((service) => {
      instanceSubscription.services[service.key] = { severities: {} };
      severities.map((severity) => {
        instanceSubscription.services[service.key].severities[severity.apiKey] = true;
      });
    });

    // Subscribe to custom with all checked, subscription is assured valid
    this.setState({
      all: false, custom: true, instanceSubscription, subscriptionValid: true
    });
  }

  clickAllServices() {
    // Check if any Service/Severity combinations are checked
    const someChecked = _.some(this.state.instanceSubscription.services, (service) => {
      return _.some((service.severities));
    });

    // Create a new subscription map
    // If no severities are checked then check all
    // If some (indeterminate) or all services are checked then check none
    const instanceSubscription = { services: {} };
    this.props.requests.instancePreview.value.Services.map((service) => {
      instanceSubscription.services[service.key] = { severities: {} };
      severities.map((severity) => {
        instanceSubscription.services[service.key].severities[severity.apiKey] = !someChecked;
      });
    });

    this.setState({ instanceSubscription });
    // setTimeout(() => this.validateSubscription(), 50);
    this.validateSubscription(instanceSubscription);
  }

  clickService(serviceKey) {
    const instanceSubscription = this.state.instanceSubscription;

    // Check if any sevices are checked
    
    const someChecked = _.some((instanceSubscription.services[serviceKey].severities));

    // Create a new subscription map
    // If no severities are checked then check all
    // If some (indeterminate) or all services are checked then check none
    const serviritySubscriptions = {};
    severities.map((severity) => {
      serviritySubscriptions[severity.apiKey] = !someChecked;
    });

    _.set(instanceSubscription, [
      'services',
      serviceKey,
      'severities',
    ], serviritySubscriptions);

    this.setState({ instanceSubscription });
    this.validateSubscription(instanceSubscription);
  }

  clickServiceSeverity(serviceKey, severity) {
    // Gets Subscription from state by path, defaults to false
    const instanceSubscription = this.state.instanceSubscription;
    const current = _.get(instanceSubscription, [
      'services',
      serviceKey,
      'severities',
      severity
    ], false);

    // Set the opposite of the current
    _.set(instanceSubscription, [
      'services',
      serviceKey,
      'severities',
      severity
    ], !current);

    this.setState({ instanceSubscription });
    this.validateSubscription(instanceSubscription);
  }

  clickSubmit() {
    this.setState({ submitted: true });

    // If email or subscription is invalid do not submit
    if (!this.state.emailValid || (this.state.custom && !this.state.subscriptionValid)) return;

    const instanceKey = this.props.instanceKey;
    const payload = {
      emailAddress: this.state.email,
      locale: this.state.language,
      subscriptions: { instances: { [instanceKey]: {} } }
    };

    // Subscribe to all if All Radio button isn checked
    // Or if all services are selected
    const all = this.state.all || _.every(this.state.instanceSubscription.services, (service) => {
      return _.every(service.severities);
    });


    // If all, subscribe to all instance notifications
    // Otherwise specified service level subscription
    if (all) {
      payload.subscriptions.instances[instanceKey].all = true;  
    } else if (this.state.custom) {
      payload.subscriptions.instances[instanceKey] = this.state.instanceSubscription;  
    }
    return this.props.requests.subscribe.handleRequest({ body: payload });
  }

  getSubscription(serviceKey, severity) {
    return _.get(this.state.instanceSubscription, [
      'services',
      serviceKey,
      'severities',
      severity
    ], false);
  }

  componentWillUnmount() {
    this.props.requests.subscribe.handleReset();
  }

  render() {
    const {
      closeModal, requests, instanceKey, isOpen
    } = this.props;
    const { emailValid, subscriptionValid, submitted } = this.state;
    const locales = requests.locales.value;
    if (!requests.instancePreview.isCompleted ||
      !requests.locales.isCompleted) return <LoadingSpinner inverse />;

    if (requests.subscribe.isCompleted) {
      return (
        <DismissModal closeModal={closeModal} />
      );
    }

    const formElementClassNames = classnames(
      'slds-form-element',
      'slds-size--1-of-1',
      'slds-medium-size--1-of-2',
      { 'slds-has-error': submitted && !emailValid }
    );

    return (
      <Modal isOpen={true} onClose={closeModal}>
        <div className='slds-modal__header'>
          <div className='slds-p-around--x-small'>
            <h2 className='slds-text-heading--medium slds-m-bottom--small'>
              <Localized
                modelName={'subscribeModal'}
                modelKey={'subscribe'}
                modelAttribute={'label'}
              />
            </h2>
            <h4 className='slds-text-heading--small'>
              <Localized
                modelName={'instance'}
                modelKey={instanceKey}
                modelAttribute={'label'} />
            </h4>
          </div>
        </div>
        <div className='slds-modal__content-footer'>
          <div className='slds-p-vertical--large slds-border--bottom slds-p-horizontal--medium slds-medium-p-horizontal--large'>
            <fieldset>
              <div className='slds-grid slds-wrap slds-medium-nowrap slds-p-bottom--large'>
                <div className={formElementClassNames}>
                  <label className='slds-form-element__label slds-text-title--caps' htmlFor='email'>
                    <Localized modelName={'subscribeModal'} modelKey={'email'} modelAttribute={'label'} />
                  </label>
                  <input
                    id='email'
                    autoFocus
                    className='slds-input'
                    type='text'
                    size='10'
                    maxLength='100'
                    onChange={this.onEmailChange}
                    value={this.state.email}
                    placeholder='email@example.com'
                  />
                  {(submitted && !emailValid) &&
                    <div id='error-message' className='slds-form-element__help'>
                      <Localized modelName={'subscribeModal'} modelKey={'invalid'} modelAttribute={'value'} />
                    </div>
                  }
                </div>
                <div className='slds-form-element slds-size--1-of-1 slds-medium-size--1-of-2 slds-p-top--medium slds-medium-p-top--reset slds-medium-p-left--medium'>
                  <label className='slds-form-element__label slds-text-title--caps' htmlFor='language'>
                    <Localized modelName={'subscribeModal'} modelKey={'language'} modelAttribute={'label'} />
                  </label>
                  <select
                    id='language'
                    className='slds-select'
                    onChange={this.onLanguageChange}
                    value={this.state.language}
                  >
                    {requests.locales.value.map((locale, index) => {
                      return <option value={locale.code} key={index}>{locale.name}</option>;
                    })}
                  </select>
                </div>
              </div>
            </fieldset>
            <fieldset>
              <div className='slds-form-element__control'>
                <span className='slds-radio slds-p-bottom--x-small' onClick={this.clickSubscribeToAll}>
                  <input
                    type='radio'
                    id='all'
                    name='subscriptionType'
                    checked={this.state.all}
                  />
                  <label className='slds-radio__label' htmlFor='all'>
                    <span className='slds-radio--faux' ></span>
                    <span className='slds-form-element__label'>
                      <span>
                        I'd like to get email notifications for all future incidents and maintenance windows on&nbsp;
                      </span>
                      <Localized modelName={'instance'} modelKey={instanceKey} modelAttribute={'label'} />
                    </span>
                  </label>
                </span>
                <span className='slds-radio' onClick={this.clickSubscribeToCustom}>
                  <input
                    type='radio'
                    id='custom'
                    name='subscriptionType'
                    checked={this.state.custom}
                  />
                  <label className='slds-radio__label' htmlFor='custom' >
                    <span className='slds-radio--faux' ></span>
                    <span className='slds-form-element__label'>
                      I'd like to customize my email notifications for future incidents and maintenance windows
                    </span>
                  </label>
                </span>
              </div>
            </fieldset>
          </div>
          {this.state.custom &&
            <SubscriptionGrid
              instanceSubscription={this.state.instanceSubscription}
              severities={severities}
              services={requests.instancePreview.value.Services}
              clickAllServices={this.clickAllServices}
              clickService={this.clickService}
              clickServiceSeverity={this.clickServiceSeverity}
              invalid={submitted && !subscriptionValid}
            />
          }
        </div>
        <div className='slds-modal__footer'>
          <div className='slds-x-small-buttons--horizontal'>
            <button
              className='slds-button slds-button--neutral'
              onClick={closeModal}
            >
              <Localized modelName='modalFooter' modelAttribute='label' modelKey='cancel' />
            </button>
            <button
              className='slds-button slds-button--brand'
              onClick={this.clickSubmit}
            >
              <Localized modelName={'subscribeModal'} modelKey={'subscribe'} modelAttribute={'label'} />
            </button>
          </div>
        </div>
      </Modal>
    );
  }
}
